import { useState } from 'react';

import { Segmented, Widget } from '../blog/Figure';

/**
 * The same three calls (list tools, call list_issues, call delete_repository)
 * against three setups, for /blog/mcp-proxy-vs-mcp-gateway. The first two
 * columns are the output of the 7-line and 14-line Node proxies shown in the
 * post, run on Node 22 on 2026-10-15. The gateway column is Statio's documented
 * behaviour (/security and /mcp-permissions). Showing all three side by side
 * is the point: the proxy does not fail, it just has nothing to decide with.
 */

type Layer = 'proxy' | 'deny' | 'gateway';

const COLS: { id: Layer; label: string }[] = [
  { id: 'proxy', label: 'Pass-through proxy' },
  { id: 'deny', label: 'Proxy with a deny list' },
  { id: 'gateway', label: 'Gateway' },
];

const ROWS: { q: string; cells: Record<Layer, string> }[] = [
  {
    q: 'Call delete_repository',
    cells: {
      proxy: 'ran delete_repository',
      deny: 'error -32001 denied',
      gateway: '403 before any credential is fetched',
    },
  },
  {
    q: 'Does tools/list still show delete_repository?',
    cells: { proxy: 'Yes', deny: 'Yes', gateway: 'Not for a caller without the grant' },
  },
  {
    q: 'Who is the rule written for?',
    cells: { proxy: 'No rule', deny: 'Every caller alike', gateway: 'A named agent' },
  },
  {
    q: 'Is the refused call recorded?',
    cells: { proxy: 'No', deny: 'Only if you add logging', gateway: 'Yes, with the agent that made it' },
  },
  {
    q: 'Where does the upstream key sit?',
    cells: {
      proxy: 'With whoever launched it',
      deny: 'With whoever launched it',
      gateway: 'In the gateway; the agent holds one token',
    },
  },
];

export function ProxyLayers() {
  const [layer, setLayer] = useState<Layer>('proxy');
  return (
    <Widget
      title="Same three calls, three setups"
      controls={
        <Segmented
          label="Setup to highlight"
          value={layer}
          onChange={setLayer}
          options={COLS.map((c) => ({ value: c.id, label: c.label }))}
        />
      }
    >
      <div className="bw-in" style={{ overflowX: 'auto' }}>
        <table className="bw-table">
          <thead>
            <tr>
              <th scope="col">Question</th>
              {COLS.map((c) => (
                <th key={c.id} scope="col" className={c.id === layer ? 'pl-on' : 'pl-off'}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r) => (
              <tr key={r.q}>
                <th scope="row">{r.q}</th>
                {COLS.map((c) => (
                  <td key={c.id} className={c.id === layer ? 'pl-on' : 'pl-off'}>
                    {r.cells[c.id]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Widget>
  );
}
